import { GOOGLE_PLACES_API_KEY, GOOGLE_PLACES_DEBOUNCE } from "@shared/env";
import { useEffect, useRef } from "react";
import useBasePlacesAutocomplete, { HookArgs } from "use-places-autocomplete";

export type FilterOptions = {
  types?: string[];
};

/*
 * Wrapper for the usePlacesAutocomplete hook.

 * Sets default values for debounce and callbackName, and handles loading of the Google maps API scripts.
 * Reduces the amount of duplicate parameters everywhere this hook is used.
 */
export default function usePlacesAutocomplete(
  args?: Omit<HookArgs, "initOnMount"> & { filterOptions?: FilterOptions }
) {
  const { init, ...placesAutocomplete } = useBasePlacesAutocomplete({
    ...args,
    debounce: args?.debounce ?? GOOGLE_PLACES_DEBOUNCE,
    initOnMount: false,
    requestOptions: {
      ...(args?.requestOptions || {}),
      // Include filterOptions into requestOptions if provided
      types: args?.filterOptions?.types,
    },
  });

  const hasCalledLoadScript = useRef(false);
  useEffect(() => {
    if (!hasCalledLoadScript.current) {
      loadScript(
        `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_PLACES_API_KEY}&libraries=places`,
        init,
        "google-places-script"
      );
      hasCalledLoadScript.current = true;
    }
  }, [init]);

  return placesAutocomplete;
}

export function loadScript(url: string, callback: () => void, id: string) {
  // Check if the script already exists
  const existingScript = document.getElementById(
    id
  ) as HTMLScriptElement | null;

  if (existingScript) {
    // If script is already loaded, execute callback immediately
    if (existingScript.dataset.loaded === "true") {
      callback();
      return;
    }

    // If script is still loading, add another load listener
    existingScript.addEventListener("load", callback);
  } else {
    // Create a new script element
    const script = document.createElement("script");
    script.type = "text/javascript";
    script.src = url;
    script.id = id;

    // Mark as loaded when complete
    script.addEventListener("load", () => {
      script.dataset.loaded = "true";
      callback();
    });

    // Append to document
    document.head.appendChild(script);
  }
}
